package dev.kuml.portal.web.i18n.legal

/**
 * The German legal-page content -- a 1:1 port of the pre-R-18 `legal/LegalScreens.kt` text into
 * the [LegalBlock] model (see this wave's plan §6.2), with exactly ONE inhaltliche Ergänzung: the
 * new Vertragssprache/Vorrang-Klausel in [terms]'s § 13 (this wave's plan, decision E-5) -- needed
 * so the AGB's own binding text, not just the English courtesy translation, states that the German
 * version controls in case of a discrepancy. Every other paragraph is unchanged.
 *
 * This is a technically accurate draft, not a substitute for legal review -- see the original
 * `LegalScreens.kt` KDoc this text was ported from.
 */
public object LegalPagesDe : LegalPages {
    override val imprint: LegalPage =
        legalPage("Impressum") {
            h("Angaben gemäß § 5 TMG")
            address()

            h("Kontakt")
            p("Telefon: +49 179 90 67 200")
            rich(txt("E-Mail: "), lnk("irakli@betchvaia.de", "mailto:irakli@betchvaia.de"))

            h("Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV")
            address(includeCountry = false)

            h("EU-Streitschlichtung")
            rich(
                txt("Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit: "),
                lnk("https://ec.europa.eu/consumers/odr/", "https://ec.europa.eu/consumers/odr/", newTab = true),
            )
            p("Unsere E-Mail-Adresse finden Sie oben im Impressum.")

            h("Verbraucherstreitbeilegung / Universalschlichtungsstelle")
            p(
                "Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer " +
                    "Verbraucherschlichtungsstelle teilzunehmen.",
            )

            h("Haftung für Inhalte")
            p(
                "Als Diensteanbieter sind wir gemäß § 7 Abs. 1 TMG für eigene Inhalte auf diesen Seiten nach den " +
                    "allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als Diensteanbieter jedoch " +
                    "nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach " +
                    "Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen. Verpflichtungen zur " +
                    "Entfernung oder Sperrung der Nutzung von Informationen nach den allgemeinen Gesetzen bleiben " +
                    "hiervon unberührt. Eine diesbezügliche Haftung ist jedoch erst ab dem Zeitpunkt der Kenntnis " +
                    "einer konkreten Rechtsverletzung möglich. Bei Bekanntwerden entsprechender Rechtsverletzungen " +
                    "werden wir diese Inhalte umgehend entfernen.",
            )

            h("Haftung für Links")
            p(
                "Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss " +
                    "haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die " +
                    "Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten " +
                    "verantwortlich.",
            )

            h("Urheberrecht")
            rich(
                txt("Der Quellcode des kUML-Projekts steht unter der "),
                lnk("Apache License 2.0", "https://www.apache.org/licenses/LICENSE-2.0", newTab = true),
                txt(
                    ". Inhalte und Werke auf diesen Seiten, die nicht ausdrücklich anders gekennzeichnet " +
                        "sind, unterliegen dem deutschen Urheberrecht.",
                ),
            )
        }

    override val privacy: LegalPage =
        legalPage("Datenschutzerklärung") {
            p(
                "Informationen zur Verarbeitung personenbezogener Daten bei der Nutzung des kUML Portal " +
                    "(app.kuml.dev) gemäß Art. 13 DSGVO.",
            )

            h("1. Verantwortlicher")
            address()
            rich(lnk("irakli@betchvaia.de", "mailto:irakli@betchvaia.de"))
            p("Telefon: +49 179 90 67 200")

            h("2. Registrierung und Nutzerkonto")
            p(
                "Bei der Registrierung erheben wir Ihre E-Mail-Adresse, ein von Ihnen gewähltes Passwort " +
                    "(dieses wird ausschließlich als Argon2id-Hashwert gespeichert, niemals im Klartext) und " +
                    "einen Anzeigenamen. Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO (Erfüllung des " +
                    "Nutzungsvertrags). Die Daten werden für die Dauer des Bestehens Ihres Kontos gespeichert. " +
                    "Eine Löschung Ihres Kontos können Sie jederzeit formlos per E-Mail an oben genannte Adresse " +
                    "beantragen; eine Selbstbedienungsfunktion zur Kontolöschung existiert derzeit nicht.",
            )

            h("3. Anmeldung (Session-Cookie)")
            p(
                "Nach dem Login setzen wir ein technisch notwendiges Cookie (KUML_PORTAL_SESSION), das Ihre " +
                    "Anmeldung über mehrere Seitenaufrufe hinweg aufrechterhält. Es ist httpOnly (per JavaScript " +
                    "nicht auslesbar), im produktiven Betrieb nur über eine verschlüsselte Verbindung " +
                    "übertragbar (Secure) und auf SameSite=Lax beschränkt. Da dieses Cookie technisch zwingend " +
                    "erforderlich ist, ist gemäß § 25 Abs. 2 Nr. 2 TTDSG keine gesonderte Einwilligung " +
                    "erforderlich. Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO. Das Cookie verliert mit " +
                    "Abmeldung oder Ablauf seine Gültigkeit.",
            )

            h("4. Projekt-, Team- und Diagrammdaten")
            p(
                "Von Ihnen angelegte Projekte (inkl. des Diagramm-Quelltextes), Projektnamen und " +
                    "-beschreibungen sowie Team-Mitgliedschaften und -Einladungen (E-Mail-Adresse der " +
                    "eingeladenen Person) werden gespeichert, solange das jeweilige Projekt bzw. Team besteht. " +
                    "Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO.",
            )

            h("5. KI-Assistent")
            p(
                "Nutzen Sie die KI-gestützte Diagrammerzeugung, wird Ihr Prompt-Text (und ggf. der bereits " +
                    "bestehende Diagramm-Quelltext Ihres Projekts) an einen externen Sprachmodell-Anbieter " +
                    "übermittelt. Welcher Anbieter das ist, legt der Betreiber serverseitig fest — infrage " +
                    "kommen Anthropic PBC, OpenAI L.L.C. und Google LLC (alle drei mit Sitz in den USA) oder " +
                    "ein selbst betriebenes, lokales Modell (Ollama), bei dem keine Daten den eigenen Server " +
                    "verlassen. Bei Nutzung eines US-Anbieters handelt es sich um eine Datenübermittlung in " +
                    "ein Drittland; sie erfolgt auf Grundlage von Standardvertragsklauseln der EU-Kommission " +
                    "bzw. eines etwaigen Angemessenheitsbeschlusses des jeweiligen Anbieters. Rechtsgrundlage " +
                    "der Übermittlung ist Art. 6 Abs. 1 lit. b DSGVO, da die KI-Generierung eine von Ihnen " +
                    "aktiv angeforderte Vertragsleistung ist. Der jeweils aktuell verwendete Anbieter kann " +
                    "beim Verantwortlichen erfragt werden.",
            )

            h("6. KI-Training und Renderer-Verbesserung")
            p(
                "Jede KI-Generierung, die Sie im Portal auslösen, wird zusätzlich zum aktuellen Stand Ihres " +
                    "Projekts als eigener Historien-Eintrag gespeichert: Zeitpunkt, Ihr Konto, das Projekt, " +
                    "Ihr Prompt-Text, der erzeugte kUML-Quelltext, die daraus gerenderte Grafik und die zu " +
                    "diesem Zeitpunkt eingesetzte Version unseres Diagramm-Renderers. Zwischenstände aus der " +
                    "Echtzeit-Zusammenarbeit werden nicht gespeichert.",
            )
            p("Diese Daten dienen zwei getrennten Zwecken, für die jeweils eine eigene Rechtsgrundlage gilt:")
            bullets(
                "Renderer-Fehlerkorrektur: Melden Sie ein Diagramm als fehlerhaft, können wir aus " +
                    "Quelltext, Grafik und Renderer-Version den Fehler reproduzieren und beheben. Ein " +
                    "Kommentarverlauf ermöglicht dabei die Rückfrage zwischen Ihnen und uns. " +
                    "Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an der " +
                    "Fehlerfreiheit und Weiterentwicklung des Dienstes). Dieser Zweck gilt für alle Pläne.",
                "Training eines eigenen KI-Modells: Generierungen aus dem kostenlosen Free-Plan können " +
                    "in das Training eines von uns selbst und ausschließlich auf unserer eigenen " +
                    "Infrastruktur betriebenen Sprachmodells einfließen. Es findet für diesen Zweck " +
                    "keine Übermittlung an einen externen Anbieter statt (abzugrenzen von Abschnitt 5, " +
                    "der die Erzeugung selbst betrifft). Rechtsgrundlage ist ebenfalls Art. 6 Abs. 1 " +
                    "lit. f DSGVO; das kostenfreie Angebot wird durch diese Nutzung mitfinanziert. " +
                    "Generierungen aus kostenpflichtigen Plänen (Pro/Team) werden für diesen Zweck nicht " +
                    "verwendet.",
                "Verstärkte Nutzung (Showcase): Nur wenn Sie dies im Profil ausdrücklich aktivieren, " +
                    "dürfen Ihre Diagramme im Trainingsdatensatz bevorzugt berücksichtigt und als " +
                    "öffentliches Beispiel (etwa auf unserer Webseite oder in Präsentationsmaterial) " +
                    "verwendet werden. Rechtsgrundlage ist ausschließlich Ihre Einwilligung nach " +
                    "Art. 6 Abs. 1 lit. a DSGVO. Diese Einwilligung ist voreingestellt nicht erteilt.",
            )
            p(
                "Speicherdauer: Nicht beanstandete Generierungen werden je Projekt automatisch auf die " +
                    "jeweils 50 jüngsten Einträge begrenzt; ältere Einträge werden gelöscht. Beanstandete " +
                    "Generierungen bleiben bis zum Abschluss der Fehlerbearbeitung erhalten. Sämtliche " +
                    "Einträge werden spätestens mit der Löschung des zugehörigen Projekts gelöscht. Die " +
                    "Löschung Ihres Kontos entfernt Ihre Autorenzuordnung aus verbleibenden Einträgen " +
                    "(auch aus solchen, die zu einem geteilten Team-Projekt gehören und daher nach Ihrem " +
                    "Ausscheiden bei Ihrem Team verbleiben) sowie Ihre eigenen Beiträge im " +
                    "Kommentarverlauf; die verbleibenden Diagramminhalte selbst werden dadurch nicht " +
                    "gelöscht, solange das zugehörige Projekt fortbesteht. Möchten Sie zusätzlich die " +
                    "Löschung des Inhalts Ihrer eigenen Generierungen, teilen Sie uns dies bei Ihrer " +
                    "Kontolöschungsanfrage (siehe Abschnitt 2) ausdrücklich mit.",
            )
            p(
                "Widerspruchsrecht (Art. 21 DSGVO): Sie können der Nutzung Ihrer Diagramme zu Trainings- " +
                    "und Korrekturzwecken jederzeit widersprechen. Dafür genügt es, in Ihrem Profil den " +
                    "Schalter „Meine Diagramme dürfen zur Korrektur von Renderer-Fehlern und zum Training " +
                    "verwendet werden\" zu deaktivieren; ein Grund ist nicht erforderlich, eine E-Mail an den " +
                    "Verantwortlichen genügt ebenfalls. Der Widerspruch wirkt sofort für alle künftigen " +
                    "Generierungen und darüber hinaus auch für bereits gespeicherte, aber noch nicht " +
                    "verwendete Generierungen: unser Auswahlverfahren prüft bei jeder Verwendung den aktuellen " +
                    "Stand Ihres Kontos. Bereits abgeschlossene Trainingsläufe lassen sich nicht rückwirkend " +
                    "rückgängig machen.",
            )
            p(
                "Widerruf der Einwilligung: Die gesonderte Showcase-Einwilligung können Sie jederzeit mit " +
                    "Wirkung für die Zukunft widerrufen, indem Sie den entsprechenden Schalter im Profil " +
                    "deaktivieren. Die Rechtmäßigkeit der bis zum Widerruf erfolgten Verarbeitung bleibt " +
                    "davon unberührt (Art. 7 Abs. 3 DSGVO). Bereits veröffentlichte Beispiele entfernen wir " +
                    "auf Ihre Anfrage hin.",
            )
            p(
                "Beide Schalter sind unabhängig voneinander und wirken sich nicht auf Ihren Funktionsumfang " +
                    "aus — insbesondere führt ein Widerspruch weder zu einer Einschränkung noch zu einer " +
                    "Verteuerung des Free-Plans.",
            )

            h("7. Zahlungsabwicklung (kostenpflichtige Pläne)")
            p(
                "Für kostenpflichtige Pläne (Pro/Team) arbeiten wir mit Lemon Squeezy (Lemon Squeezy, Inc., " +
                    "USA) als Merchant of Record zusammen. Beim Abschluss oder der Verwaltung eines " +
                    "Abonnements werden Sie auf eine von Lemon Squeezy selbst gehostete Checkout- bzw. " +
                    "Kundenportal-Seite weitergeleitet — dort verarbeitet Lemon Squeezy Ihre Zahlungsdaten " +
                    "als eigenständiger, datenschutzrechtlich Verantwortlicher; maßgeblich ist Lemon Squeezys " +
                    "eigene Datenschutzerklärung. Wir selbst speichern ausschließlich Ihren Plan-/" +
                    "Abonnementstatus sowie die von Lemon Squeezy vergebene Kunden-/Abonnement-ID — niemals " +
                    "Kreditkarten- oder sonstige Zahlungsdaten. Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO.",
            )

            h("8. Hosting / Server-Logfiles")
            p("Das kUML Portal wird gehostet bei:")
            lines("netcup GmbH", "Daimlerstraße 25", "76185 Karlsruhe", "Deutschland")
            p(
                "Beim Zugriff auf app.kuml.dev werden durch den Webserver automatisch technische Daten in " +
                    "Server-Logfiles erfasst (IP-Adresse, Datum/Uhrzeit, aufgerufene Adresse, HTTP-Statuscode, " +
                    "Referrer, User-Agent). Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO (berechtigtes " +
                    "Interesse an einem sicheren und stabilen Betrieb). Mit dem Hoster netcup besteht ein " +
                    "Auftragsverarbeitungsvertrag gemäß Art. 28 DSGVO.",
            )

            h("9. Kein Tracking, keine Werbung")
            p(
                "Das Portal setzt außer dem in Abschnitt 3 genannten Session-Cookie keine weiteren Cookies " +
                    "ein, verwendet kein Web-Analytics (kein Google Analytics, kein Matomo) und keine " +
                    "Werbeskripte.",
            )
            p(
                "Für die reine Sprachauswahl (Deutsch/Englisch) speichert das Portal im Browser-" +
                    "localStorage Ihrer Endeinrichtung unter dem Schlüssel kportal.lang ausschließlich den " +
                    "Wert de oder en. Dies ist technisch zwingend erforderlich für die von Ihnen selbst " +
                    "angeforderte Funktion (Beibehaltung Ihrer gewählten Sprache über mehrere Seitenaufrufe " +
                    "hinweg) und daher ist gemäß § 25 Abs. 2 Nr. 2 TTDSG keine gesonderte Einwilligung " +
                    "erforderlich. Die Angabe hat keinen Personenbezug und wird zu keinem Zeitpunkt an " +
                    "unseren Server übermittelt.",
            )

            h("10. Ihre Rechte")
            p("Sie haben gegenüber dem Verantwortlichen folgende Rechte hinsichtlich der Sie betreffenden personenbezogenen Daten:")
            bullets(
                "Auskunft (Art. 15 DSGVO)",
                "Berichtigung (Art. 16 DSGVO)",
                "Löschung (Art. 17 DSGVO)",
                "Einschränkung der Verarbeitung (Art. 18 DSGVO)",
                "Datenübertragbarkeit (Art. 20 DSGVO)",
                "Widerspruch gegen die Verarbeitung (Art. 21 DSGVO)",
            )
            rich(txt("Zur Ausübung genügt eine formlose E-Mail an "), lnk("irakli@betchvaia.de", "mailto:irakli@betchvaia.de"), txt("."))

            h("11. Beschwerderecht bei der Aufsichtsbehörde")
            p(
                "Sie haben das Recht, sich bei einer Datenschutz-Aufsichtsbehörde über die Verarbeitung " +
                    "Ihrer personenbezogenen Daten zu beschweren (Art. 77 DSGVO). Zuständig ist:",
            )
            lines("Die Landesbeauftragte für den Datenschutz Niedersachsen", "Prinzenstraße 5", "30159 Hannover")
            rich(lnk("lfd.niedersachsen.de", "https://lfd.niedersachsen.de", newTab = true))

            h("12. Aktualität und Änderung dieser Datenschutzerklärung")
            p(
                "Diese Datenschutzerklärung hat den Stand August 2026. Durch die Weiterentwicklung des Portals " +
                    "kann eine Anpassung erforderlich werden.",
            )

            h("13. Freigabe-Links")
            p(
                "Sie können ein einzelnes Diagramm über einen Freigabe-Link mit Dritten teilen. Ein Betrachter, " +
                    "der einen solchen Link öffnet, sieht ausschließlich das Diagramm selbst — weder Projektname " +
                    "noch Teamname noch Ihre Kontodaten werden ihm angezeigt oder über die zugehörigen API-Routen " +
                    "ausgeliefert. Ist der Link passwortgeschützt, wird das eingegebene Passwort serverseitig " +
                    "geprüft (Argon2id-Hash) und nicht im Klartext gespeichert.",
            )
            p(
                "Gespeichert wird zu jedem Freigabe-Link: der SHA-256-Hash des Link-Tokens (nicht der Token " +
                    "selbst), optional der Argon2id-Hash eines Passworts, der Erstellungszeitpunkt, ein optionales " +
                    "Ablaufdatum und der Zeitpunkt des letzten Aufrufs. Die IP-Adresse eines Betrachters wird " +
                    "NICHT gespeichert. Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO (Erfüllung der von Ihnen " +
                    "aktiv angeforderten Freigabe-Funktion). Sie können einen Freigabe-Link jederzeit widerrufen; " +
                    "er wird dann samt Hash unwiderruflich gelöscht.",
            )

            h("14. Soziale Anmeldung (Google / GitHub)")
            p(
                "Statt mit E-Mail-Adresse und Passwort können Sie sich auch über ein Google- oder GitHub-Konto " +
                    "registrieren bzw. anmelden. Dabei werden Sie zur Anmeldeseite des gewählten Anbieters " +
                    "weitergeleitet und melden sich dort mit Ihren eigenen Zugangsdaten an. Nach Ihrer " +
                    "Zustimmung übermittelt uns der Anbieter: eine stabile, providerseitige Konto-Kennung " +
                    "(bei Google die sogenannte „sub\"-Kennung, bei GitHub Ihre numerische Nutzer-ID), die bei " +
                    "diesem Anbieter hinterlegte primäre E-Mail-Adresse samt deren dortigem " +
                    "Verifizierungsstatus sowie Ihren dort hinterlegten Anzeigenamen. Ein Profilbild wird " +
                    "nicht übernommen. Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO, da Sie diesen " +
                    "Anmeldeweg aktiv selbst auswählen.",
            )
            p(
                "Anbieter sind Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland (Teil " +
                    "der Google LLC, USA) sowie GitHub, Inc., 88 Colin P. Kelly Jr. Street, San Francisco, " +
                    "USA (Teil der Microsoft Corporation). Soweit dabei Daten in die USA übermittelt werden, " +
                    "handelt es sich um eine Datenübermittlung in ein Drittland; sie erfolgt auf Grundlage von " +
                    "Standardvertragsklauseln der EU-Kommission bzw. eines etwaigen " +
                    "Angemessenheitsbeschlusses des jeweiligen Anbieters (vgl. Abschnitt 5).",
            )
            p(
                "Dauerhaft gespeichert werden ausschließlich: der Anbieter, die stabile Konto-Kennung, die " +
                    "zum Verknüpfungszeitpunkt gemeldete E-Mail-Adresse nebst damaligem " +
                    "Verifizierungsstatus (rein zu Nachweis-/Support-Zwecken, kein fortlaufend " +
                    "abgeglichener Wert), der Verknüpfungszeitpunkt sowie der Zeitpunkt der letzten " +
                    "Anmeldung über diesen Weg. Zugriffs- oder Refresh-Token des Anbieters werden zu keinem " +
                    "Zeitpunkt gespeichert; sie werden im Zuge der Anmeldung einmalig verwendet und danach " +
                    "verworfen.",
            )
            p(
                "Sie können eine bestehende Verknüpfung jederzeit in Ihrem Profil aufheben, sofern für Ihr " +
                    "Konto mindestens ein weiterer Anmeldeweg besteht (ein gesetztes Passwort oder ein " +
                    "zweiter verknüpfter Anbieter) — andernfalls setzen Sie zunächst über „Passwort " +
                    "vergessen\" ein Passwort.",
            )
        }

    override val terms: LegalPage =
        legalPage("Allgemeine Geschäftsbedingungen (AGB)") {
            p("Stand: August 2026")

            h("1. Geltungsbereich, Vertragspartner")
            p(
                "Diese AGB gelten für die Nutzung des kUML Portal (app.kuml.dev), eines kostenpflichtigen " +
                    "Online-Dienstes zur Modellierung, gemeinsamen Bearbeitung und KI-gestützten Erzeugung von " +
                    "UML-/kUML-Diagrammen. Vertragspartner ist:",
            )
            address()
            rich(lnk("irakli@betchvaia.de", "mailto:irakli@betchvaia.de"))
            p(
                "Diese AGB gelten sowohl gegenüber Verbrauchern (§ 13 BGB) als auch gegenüber Unternehmern " +
                    "(§ 14 BGB). Abweichende, entgegenstehende oder ergänzende Allgemeine Geschäftsbedingungen " +
                    "des Nutzers werden nicht Vertragsbestandteil, es sei denn, ihrer Geltung wird ausdrücklich " +
                    "schriftlich zugestimmt.",
            )

            h("2. Leistungsbeschreibung")
            p(
                "Das kUML Portal bietet, je nach gewähltem Plan (Free/Pro/Team), unter anderem: Anlegen und " +
                    "Verwalten von Diagramm-Projekten, KI-gestützte Diagrammerzeugung aus Freitext-Prompts, " +
                    "Echtzeit-Zusammenarbeit mehrerer Nutzer an einem Diagramm sowie Team-Verwaltung. Der " +
                    "konkrete Funktions- und Kontingentumfang der einzelnen Pläne (Projekt-Obergrenzen, " +
                    "KI-Generierungen pro Monat, Team-Größe) sowie die jeweils aktuellen Preise werden beim " +
                    "Buchungsvorgang und im Nutzerkonto angezeigt und sind nicht Bestandteil dieser AGB, um " +
                    "eine Diskrepanz zwischen den hier festgehaltenen Bedingungen und den tatsächlich " +
                    "angebotenen Konditionen zu vermeiden.",
            )

            h("3. Vertragsschluss")
            p(
                "Mit der Registrierung eines kostenlosen Free-Kontos kommt ein Nutzungsvertrag zu den in " +
                    "Ziffer 2 beschriebenen Bedingungen zustande. Für kostenpflichtige Pläne (Pro/Team) kommt " +
                    "der Vertrag erst mit erfolgreichem Abschluss des Bestellvorgangs über die " +
                    "Zahlungsabwicklung von Lemon Squeezy (siehe Ziffer 4) zustande.",
            )

            h("4. Preise, Zahlung, Zahlungsabwicklung über Lemon Squeezy")
            p(
                "Für kostenpflichtige Pläne arbeiten wir mit Lemon Squeezy (Lemon Squeezy, Inc., USA) als " +
                    "Merchant of Record zusammen. Der Nutzungsvertrag über die Portal-Dienstleistung selbst " +
                    "kommt mit dem oben genannten Vertragspartner zustande — Lemon Squeezy übernimmt " +
                    "ausschließlich die Zahlungsabwicklung, tritt dabei rechtlich als Verkäufer der Zahlung auf " +
                    "und ist insoweit für Rechnungsstellung, Umsatzsteuerabführung und Zahlungsdaten " +
                    "verantwortlich; es gelten ergänzend Lemon Squeezys eigene Geschäftsbedingungen für den " +
                    "Zahlungsvorgang. Alle Preise verstehen sich inklusive der jeweils geltenden gesetzlichen " +
                    "Umsatzsteuer, sofern anwendbar.",
            )

            h("5. Laufzeit und Kündigung kostenpflichtiger Pläne")
            p(
                "Kostenpflichtige Abonnements laufen bis auf Widerruf und sind monatlich zum Ende des " +
                    "jeweiligen Abrechnungszeitraums kündbar. Die Kündigung erfolgt über das " +
                    "Lemon-Squeezy-Kundenportal, erreichbar über das eigene Nutzerkonto. Nach Kündigung bleibt " +
                    "der Zugang bis zum Ende des bereits bezahlten Abrechnungszeitraums bestehen; danach wird " +
                    "das Konto automatisch auf den Free-Plan zurückgestuft, nicht gelöscht.",
            )

            h("6. Widerrufsrecht für Verbraucher")
            p(
                "Verbrauchern steht grundsätzlich ein gesetzliches Widerrufsrecht bei Fernabsatzverträgen zu. " +
                    "Das Widerrufsrecht erlischt bei Verträgen über digitale Inhalte/Dienstleistungen " +
                    "vorzeitig, wenn wir mit der Ausführung des Vertrags begonnen haben, nachdem der " +
                    "Verbraucher ausdrücklich zugestimmt hat, dass wir vor Ablauf der Widerrufsfrist mit der " +
                    "Ausführung beginnen, und der Verbraucher seine Kenntnis davon bestätigt hat, dass er durch " +
                    "seine Zustimmung mit Beginn der Ausführung sein Widerrufsrecht verliert (§ 356 Abs. 5 " +
                    "BGB). Bei der Buchung eines kostenpflichtigen Plans wird diese Zustimmung gesondert " +
                    "eingeholt.",
            )

            h("7. Nutzungsrechte an eigenen Inhalten")
            p(
                "Alle Rechte an den vom Nutzer erstellten Diagrammen, Prompts und sonstigen Inhalten " +
                    "verbleiben beim Nutzer. Der Nutzer räumt uns lediglich die für die Erbringung der " +
                    "Portal-Dienstleistung erforderlichen, einfachen Nutzungsrechte ein — insbesondere zur " +
                    "Speicherung, Anzeige, Verarbeitung durch den vom Betreiber gewählten KI-Anbieter (siehe " +
                    "Datenschutzerklärung, Abschnitt „KI-Assistent\") sowie zur gemeinsamen Bearbeitung durch " +
                    "von ihm eingeladene Team-Mitglieder, sowie, im Umfang der Datenschutzerklärung, " +
                    "Abschnitt „KI-Training und Renderer-Verbesserung\", zur Fehlerbehebung und " +
                    "Modellverbesserung. Diese Nutzungsrechte enden mit Löschung des jeweiligen Inhalts bzw. " +
                    "Beendigung des Vertrags.",
            )

            h("8. Pflichten des Nutzers")
            p(
                "Der Nutzer verpflichtet sich, bei der Registrierung wahrheitsgemäße Angaben zu machen, seine " +
                    "Zugangsdaten geheim zu halten und uns über eine missbräuchliche Nutzung seines Kontos " +
                    "unverzüglich zu informieren. Der Nutzer darf über das Portal keine rechtswidrigen Inhalte " +
                    "erstellen oder verbreiten.",
            )
            p(
                "Insbesondere zu personenbezogenen Daten Dritter: Enthält ein vom Nutzer erstelltes Diagramm " +
                    "personenbezogene Daten Dritter (z. B. ein Organigramm mit echten Namen), ist der Nutzer " +
                    "allein dafür verantwortlich, dass hierfür eine eigene datenschutzrechtliche " +
                    "Rechtsgrundlage vorliegt. Der Nutzer stellt uns von Ansprüchen Dritter frei, die aus einer " +
                    "Verletzung dieser Pflicht durch den Nutzer resultieren.",
            )

            h("9. Verfügbarkeit")
            p(
                "Wir bemühen uns um eine möglichst unterbrechungsfreie Verfügbarkeit des Portals, garantieren " +
                    "jedoch keine bestimmte Verfügbarkeit. Wartungsarbeiten, die zu vorübergehenden " +
                    "Einschränkungen führen können, werden nach Möglichkeit angekündigt.",
            )

            h("10. Haftung")
            p(
                "Wir haften unbeschränkt für Vorsatz und grobe Fahrlässigkeit sowie nach Maßgabe des " +
                    "Produkthaftungsgesetzes, bei der Verletzung von Leben, Körper oder Gesundheit sowie im " +
                    "Umfang einer von uns übernommenen Garantie. Bei leicht fahrlässiger Verletzung " +
                    "wesentlicher Vertragspflichten (Kardinalpflichten), deren Erfüllung die ordnungsgemäße " +
                    "Durchführung des Vertrags überhaupt erst ermöglicht und auf deren Einhaltung der Nutzer " +
                    "regelmäßig vertrauen darf, ist die Haftung auf den bei Vertragsschluss vorhersehbaren, " +
                    "vertragstypischen Schaden begrenzt. Im Übrigen ist die Haftung für leicht fahrlässige " +
                    "Pflichtverletzungen ausgeschlossen.",
            )

            h("11. Datenschutz")
            rich(
                txt("Informationen zur Verarbeitung personenbezogener Daten finden Sie in unserer "),
                lnk("Datenschutzerklärung", "/datenschutz"),
                txt("."),
            )

            h("12. Änderungen dieser AGB")
            p(
                "Wir können diese AGB mit Wirkung für die Zukunft ändern, soweit dies zur Anpassung an " +
                    "geänderte rechtliche oder technische Rahmenbedingungen erforderlich ist oder das " +
                    "bestehende Vertragsgefüge nicht zum Nachteil des Nutzers verändert. Über Änderungen " +
                    "informieren wir per E-Mail an die im Konto hinterlegte Adresse, mit einer Frist von " +
                    "mindestens vier Wochen zum Wirksamwerden. Widerspricht der Nutzer nicht innerhalb dieser " +
                    "Frist, gelten die geänderten AGB als angenommen; auf diese Folge weisen wir in der " +
                    "Änderungsmitteilung gesondert hin. Im Fall eines Widerspruchs bleibt das Recht zur " +
                    "ordentlichen Kündigung unberührt.",
            )

            h("13. Schlussbestimmungen")
            p(
                "Es gilt deutsches Recht unter Ausschluss des UN-Kaufrechts. Ist der Nutzer Kaufmann im Sinne " +
                    "des Handelsgesetzbuchs, juristische Person des öffentlichen Rechts oder " +
                    "öffentlich-rechtliches Sondervermögen, ist Braunschweig Gerichtsstand für alle " +
                    "Streitigkeiten aus diesem Vertragsverhältnis. Gegenüber Verbrauchern gilt dies nur im " +
                    "Rahmen der gesetzlichen Vorschriften. Sollten einzelne Bestimmungen dieser AGB unwirksam " +
                    "sein oder werden, bleibt die Wirksamkeit der übrigen Bestimmungen hiervon unberührt.",
            )
            // R-18, decision E-5: the only inhaltliche Änderung to the binding German text in this
            // wave -- without this clause IN the binding text itself, the English courtesy
            // translation's own precedence notice (LegalPagesEn.terms) would have no anchor in the
            // contract that actually governs.
            p(
                "Diese AGB werden in deutscher Sprache geschlossen. Eine etwaige englischsprachige Fassung " +
                    "dient ausschließlich der Information; bei Abweichungen ist ausschließlich die deutsche " +
                    "Fassung maßgeblich.",
            )
        }
}

private fun MutableList<LegalBlock>.address(includeCountry: Boolean = true) {
    if (includeCountry) {
        lines("Irakli Betchvaia", "Wendenring 22", "38114 Braunschweig", "Deutschland")
    } else {
        lines("Irakli Betchvaia", "Wendenring 22", "38114 Braunschweig")
    }
}
